<script setup lang="ts">
import BooleanIcon from '@/components/shared/BooleanIcon.vue';
import DeleteConfirmationDialog from '@/components/DeleteConfirmationDialog.vue';
import ButtonContent from '@/components/devextreme/ButtonContent.vue';
import UserEditPopup from '@/components/maintenance/UserEditPopup.vue';
import { useMaintenanceDelete } from '@/composables/useMaintenanceDelete';
import { RoleGroups } from '@/constants/Roles';
import { removedMessage } from '@/helpers/notificationMessages';
import AppIcon from '@/components/shared/AppIcon.vue';
import { isMicrosoftSignInEnabled, MicrosoftProvider } from '@/services/entra/microsoftSignIn';
import { t } from '@/i18n';
import { dataSource, useUserService } from '@/services/api/userService';
import { useUserManageStore } from '@/services/stores/userManageStore';
import type { IUserGridResponse } from '@/types/interfaces/MaintenanceTypes';
import Icons from '@/constants/Icons';
import DxButton from 'devextreme-vue/button';
import { DxColumn, DxDataGrid, DxToolbar, DxItem } from 'devextreme-vue/data-grid';
import { ref, shallowRef } from 'vue';

definePage({
  name: 'MaintenanceUsers',
  meta: {
    title: 'maintenance.tabs.users',
    roles: RoleGroups.Maintenance
  }
});

const usersGridRef = shallowRef<DxDataGrid>();
const userEditPopupRef = ref<typeof UserEditPopup>();
const deleteDialogRef = ref<InstanceType<typeof DeleteConfirmationDialog>>();
const store = useUserManageStore();

async function confirmDelete(data: IUserGridResponse): Promise<void> {
  await deleteDialogRef?.value?.show(data, `${data.userName} - ${data.displayName}`);
}

// As on the other maintenance pages: the dialog carries the row, so nothing is looked up (a lookup of a row that
// left the grid threw and left the dialog loading), and it closes after every attempt.
const api = useUserService();
const deleteUser = useMaintenanceDelete({
  dataSource,
  dialog: () => deleteDialogRef.value,
  remove: ({ id }: IUserGridResponse) => api.deleteUser(id),
  key: ({ id }) => id,
  removedMessage: ({ userName }) => removedMessage(t('entities.user'), userName)
});
</script>

<template>
  <UserEditPopup ref="userEditPopupRef" />
  <DeleteConfirmationDialog ref="deleteDialogRef" :entity="$t('entities.user')"
      @confirm="deleteUser" />

  <DxDataGrid id="usersGrid" ref="usersGridRef" :data-source="store.dataSource">
    <DxToolbar>
      <DxItem location="before" template="title-template" />
      <DxItem location="before" template="add-button-template" />
      <DxItem location="after" template="refresh-button-template" />
      <DxItem :locate-in-menu="'auto'" location="after" name="searchPanel" />
    </DxToolbar>

    <DxColumn :sort-index="0" :sort-order="'asc'" data-field="userName" />
    <DxColumn data-field="displayName" />
    <DxColumn data-field="email" />
    <DxColumn :visible="isMicrosoftSignInEnabled()" data-field="entraAccount" :caption="$t('labels.externalAccount', { provider: MicrosoftProvider })" cell-template="microsoft-cell-template" />
    <DxColumn data-field="lastLoginAt" :data-type="'datetime'" format="yyyy-MMM-dd HH:mm:ss" width="200px" />
    <DxColumn data-field="twoFactorEnabled" :caption="$t('labels.twoFactor')" cell-template="boolean-cell-template" :false-text="$t('common.no')" :true-text="$t('common.yes')" width="125px" />
    <DxColumn data-field="lockedOut" :caption="$t('labels.locked')" cell-template="boolean-cell-template" :false-text="$t('common.no')" :true-text="$t('common.yes')" width="125px" />
    <DxColumn data-field="lockoutEnd" :visible="false" />
    <DxColumn data-field="enabled" cell-template="boolean-cell-template" :false-text="$t('common.no')" :true-text="$t('common.yes')" width="125px" />
    <DxColumn :visible="false" type="adaptive" />
    <DxColumn :alignment="'center'" :allow-filtering="false" :allow-sorting="false" caption="" cell-template="command-cell-template"
        data-field="id" name="command-column" width="100px" />

    <template #title-template>
      <span class="h3 fw-semibold">{{ $t('maintenance.maintain', { entities: $t('maintenance.tabs.users') }) }}</span>
    </template>

    <template #add-button-template>
      <DxButton class="ms-3" type="success" @click="userEditPopupRef?.addUser()">
        <ButtonContent :icon="Icons.Plus" :text="$t('common.addEntity', { entity: $t('entities.user') })" />
      </DxButton>
    </template>

    <template #refresh-button-template>
      <DxButton type="primary" @click="usersGridRef?.instance?.refresh()">
        <ButtonContent :icon="Icons.ArrowsRotate" />
      </DxButton>
    </template>

    <template #boolean-cell-template="{ data }">
      <BooleanIcon :value="data.value" />
    </template>

    <!-- Pending: assigned, but the user has not signed in with Microsoft yet. -->
    <template #microsoft-cell-template="{ data }">
      <span v-if="data.value" :class="data.data.entraLinked ? '' : 'text-body-secondary fst-italic'">
        <AppIcon :icon="Icons.Microsoft" class="me-1" />{{ data.value }}
        <small v-if="!data.data.entraLinked">({{ $t('users.externalPending') }})</small>
      </span>
    </template>

    <template #command-cell-template="{ data }">
      <DxButton type="primary" @click="userEditPopupRef?.editUser(data.value)">
        <ButtonContent :icon="Icons.Pencil" />
      </DxButton>
      <DxButton type="danger" @click="confirmDelete(data.data)">
        <ButtonContent :icon="Icons.Trash" />
      </DxButton>
    </template>
  </DxDataGrid>
</template>

<style scoped>

</style>